// ws/room-ws.js: M3-13a client-side WebSocket service.
//
// Responsibilities:
//   • open + maintain a single WS connection to the server
//   • wrap outbound commands with protocol_version, client_command_seq,
//     and a generated idempotency_key (if the caller didn't provide one)
//   • surface inbound events via onEvent
//   • track connection status ('idle' | 'connecting' | 'open' |
//     'reconnecting' | 'closed') and surface via onStatusChange
//   • PING every PING_INTERVAL_MS with client_ts so the server can
//     compute RTT and the client can detect lagging links
//   • on unexpected close, reconnect with exponential backoff
//     (1s, 2s, 4s, 8s, cap 30s); after re-open, replay JOIN_ROOM with
//     the cached room_code so the server can reattach the occupant
//
// Out of scope here: derived room state. M3-13b's useRoomWs hook owns
// the reducer that maps events to {occupants, settings, phase, ...}.
//
// Testability:
//   • WebSocketCtor is injectable (`opts.WebSocketCtor`) so node:test
//     can wire the `ws` package's client.
//   • scheduler / canceller / now are all injectable so tests can drive
//     deterministic timing for backoff + PING without sleeping.

import { generateUUIDv4 } from '../identity/uuid.js';

export const PROTOCOL_VERSION = 1;
// Heartbeat MUST be well under the server's lagging threshold
// (room-adapter laggingThresholdMs = 30s) so an idle-but-alive player is never
// falsely flagged "lagging" → "disconnected" while thinking over a bid
// (bug #16). 5s gives ~6 pings before the threshold and snappier real-drop
// detection.
export const PING_INTERVAL_MS = 5_000;
// A socket that delivers nothing (no PONG, no other frame) for two ping
// intervals plus a 2 s margin is dead, even if the browser still says it
// is open (a silent network drop). The service closes it and retries.
export const DEAD_AFTER_MS = 2 * PING_INTERVAL_MS + 2_000;
const DEAD_CHECK_MS = 1_000;
// A game command (bid, discard, play, ready) normally gets its answer
// (COMMAND_ACCEPTED, an error, or the game event it caused) well inside
// this. With no answer by then, the service sends a PING at once.
export const COMMAND_ANSWER_TIMEOUT_MS = 3_000;
// No server frame at all (PONG or anything else) this long after that
// PING: the socket is dead and is dropped.
export const COMMAND_PROBE_TIMEOUT_MS = 2_000;
// A live socket waits for the answer: the server sends its own timeout
// answer 10 s after the command (room-adapter gameCommandTimeoutMs).
// Still no answer this long after the send: the socket is dropped.
export const COMMAND_LIVE_LIMIT_MS = 12_000;
// After a reattach, the game snapshot (GAME_INITIALIZED) ends the wait
// for a command sent on the old socket. If it has not come by this long
// after the new socket opened, the wait ends anyway, with a warning.
export const REATTACH_DECIDE_MS = 5_000;
// A connect attempt that has not opened by now is given up and retried.
export const CONNECT_TIMEOUT_MS = 10_000;
export const BACKOFF_SCHEDULE_MS = [1_000, 2_000, 4_000, 8_000, 16_000, 30_000];

/**
 * @typedef {'idle'|'connecting'|'open'|'reconnecting'|'closed'} ConnectionStatus
 *
 * @typedef {{ client_uuid:string, display_name:string, avatar_color:string }} Identity
 *
 * @typedef {{
 *   url:                string,
 *   identity:           Identity,
 *   onEvent?:           (event: any) => void,
 *   onError?:           (err: { code:string, message:string, recoverable:boolean }) => void,
 *   onStatusChange?:    (status: ConnectionStatus) => void,
 *   WebSocketCtor?:     typeof WebSocket,
 *   pingIntervalMs?:    number,
 *   scheduler?:         typeof setTimeout,
 *   canceller?:         typeof clearTimeout,
 *   intervalScheduler?: typeof setInterval,
 *   intervalCanceller?: typeof clearInterval,
 *   now?:               () => number,
 * }} CreateOptions
 */

/**
 * @param {CreateOptions} opts
 */
export function createRoomWsService(opts) {
  const {
    url,
    identity,
    onEvent           = () => {},
    onError           = () => {},
    onStatusChange    = () => {},
    pingIntervalMs    = PING_INTERVAL_MS,
    scheduler         = setTimeout,
    canceller         = clearTimeout,
    intervalScheduler = setInterval,
    intervalCanceller = clearInterval,
    now               = () => Date.now(),
  } = opts;
  /** @type {any} */
  const explicitCtor = opts.WebSocketCtor || null;
  function resolveCtor() {
    // Resolved per attempt so a page can replace window.WebSocket (e.g. to
    // force WS_CONSTRUCT_FAILED) after the service was created. An explicit
    // injected ctor still wins for Node tests.
    const ctor = explicitCtor || /** @type {any} */ (globalThis).WebSocket;
    if (!ctor) throw new Error('room-ws: no WebSocket constructor available');
    return ctor;
  }

  // The one current socket. Handlers of any older socket are ignored.
  /** @type {WebSocket|null} */
  let ws = null;
  let wsOpen = false;              // the current socket fired 'open'
  let lastFrameAt = 0;             // now() of the last server frame
  let unansweredPings = 0;         // PINGs sent since the last frame
  /** @type {any} */
  let deadCheckHandle = null;
  /** @type {ConnectionStatus} */
  let status = 'idle';
  let seq = 1;                     // monotonic, survives reconnect
  let backoffStep = 0;
  /** @type {string|null} */
  let lastRoomCode = null;
  /** @type {any[]} */
  let outboundQueue = [];          // commands sent before WS is open
  /** @type {any} */
  let pingHandle = null;
  /** @type {any} */
  let reconnectHandle = null;
  /** @type {any} */
  let connectTimeoutHandle = null;
  // The one game command waiting for its answer, and the socket it went on.
  /** @type {{ type: string, idempotency_key: string }|null} */
  let pendingCommand = null;
  /** @type {any} */
  let pendingSocket = null;
  /** @type {any} */
  let pendingTimeoutHandle = null;   // 3 s: send the probe PING
  /** @type {any} */
  let pendingProbeHandle = null;     // 2 s after the probe: no frame, drop
  /** @type {any} */
  let pendingLimitHandle = null;     // 12 s after the send: drop
  /** @type {any} */
  let reattachDecideHandle = null;   // 5 s after a reattach: end the wait
  /**
   * @typedef {{ command: { type: string, idempotency_key: string }, answer: any, reason: string }} Settled
   * @type {Set<(pending: { type: string, idempotency_key: string }|null, settled?: Settled) => void>}
   */
  const pendingSubscribers = new Set();
  let reconnectDueAt = null;
  let reconnectAttempt = 0;
  let manualClose = false;         // disconnect() vs unexpected close
  /** @type {Map<string, any>} */
  const pendingByKey = new Map();  // idempotency_key → last command sent
  // M4-10: extra subscribers (e.g. useGameWs). The primary onEvent
  // callback still fires for everything; subscribers see the same frames.
  /** @type {Set<(ev:any) => void>} */
  const extraSubscribers = new Set();
  // Game events can arrive before the game view subscribes, both after
  // GAME_STARTING and during a fresh reattach with no GAME_STARTING.
  // Replay those frames to the first subscriber that mounts.
  /** @type {any[]} */
  let gameReplayBuffer = [];
  /** Cap for the game replay buffer (replay-buffer hygiene). */
  const GAME_REPLAY_BUFFER_CAP = 200;

  function clearGameReplayBuffer() {
    gameReplayBuffer = [];
  }

  function pushGameReplayFrame(frame) {
    // A new game supersedes everything buffered from the previous one.
    if (frame?.type === 'GAME_INITIALIZED') {
      gameReplayBuffer = [];
    }
    gameReplayBuffer.push(frame);
    if (gameReplayBuffer.length > GAME_REPLAY_BUFFER_CAP) {
      const excess = gameReplayBuffer.length - GAME_REPLAY_BUFFER_CAP;
      const dropped = gameReplayBuffer.slice(0, excess);
      gameReplayBuffer = gameReplayBuffer.slice(excess);
      const names = dropped.map((f) => f?.type ?? 'unknown').join(',');
      console.warn(`room-ws: replay buffer overflow, dropped ${excess} oldest frame(s): ${names}`);
    }
  }
  // M4-14: clock-offset state. Each PONG carries the echoed client_ts
  // and the server's current time; we compute a sample then smooth via
  // EWMA(α=0.4). Surfaced via getClockOffset() / subscribeClockOffset().
  /** @type {number} */ let clockOffsetMs = 0;
  /** @type {number} */ let clockRttMs   = 0;
  /** @type {Set<(o:{clockOffsetMs:number, rttMs:number}) => void>} */
  const clockOffsetSubscribers = new Set();
  // C3 (2026-06-11): connection-status subscribers, so views can render a
  // "Connection lost, reconnecting…" banner + freeze input. The
  // constructor-time onStatusChange callback stays for existing callers.
  /** @type {Set<(status: string) => void>} */
  const statusSubscribers = new Set();

  function setStatus(next, force = false) {
    if (status === next && !force) return;
    status = next;
    try { onStatusChange(status); } catch (e) { console.warn('[room-ws] status listener threw (onStatusChange):', e); }
    for (const listener of statusSubscribers) {
      try { listener(status); } catch (e) { console.warn('[room-ws] status subscriber threw:', e); }
    }
  }

  /**
   * Subscribe to connection-status changes ('idle' | 'connecting' |
   * 'open' | 'reconnecting' | 'closed'). Fires the current status
   * immediately so subscribers never start stale. Returns unsubscribe.
   * @param {(status: string) => void} listener
   */
  function subscribeStatus(listener) {
    statusSubscribers.add(listener);
    try { listener(status); } catch (e) { console.warn('[room-ws] status subscriber threw (initial):', e); }
    return () => { statusSubscribers.delete(listener); };
  }

  /**
   * M4-14: feed a PONG into the clock-offset estimator. EWMA α=0.4
   * (synthesis §2 + plan §M4-14 ACs).
   *   rtt    = recv_ts  - client_ts
   *   sample = server_ts - client_ts - rtt/2
   *
   * @param {{ client_ts: number, server_ts: number }} pongPayload
   * @param {number} recvTs the local Date.now() at message receipt
   */
  function ingestPong(pongPayload, recvTs) {
    if (typeof pongPayload?.client_ts !== 'number' || typeof pongPayload?.server_ts !== 'number') return;
    const sampleRtt = Math.max(0, recvTs - pongPayload.client_ts);
    const sampleOffset = pongPayload.server_ts - pongPayload.client_ts - sampleRtt / 2;
    const alpha = 0.4;
    // First sample seeds the average exactly; later samples blend in.
    if (clockRttMs === 0 && clockOffsetMs === 0) {
      clockRttMs = sampleRtt;
      clockOffsetMs = sampleOffset;
    } else {
      clockRttMs    = alpha * sampleRtt    + (1 - alpha) * clockRttMs;
      clockOffsetMs = alpha * sampleOffset + (1 - alpha) * clockOffsetMs;
    }
    for (const sub of clockOffsetSubscribers) {
      try { sub({ clockOffsetMs, rttMs: clockRttMs }); } catch (e) { console.warn('[room-ws] clock-offset subscriber threw:', e); }
    }
  }

  function clearTimers() {
    if (pingHandle) { intervalCanceller(pingHandle); pingHandle = null; }
    if (deadCheckHandle) { intervalCanceller(deadCheckHandle); deadCheckHandle = null; }
    if (reconnectHandle) { canceller(reconnectHandle); reconnectHandle = null; reconnectDueAt = null; }
    if (connectTimeoutHandle) { canceller(connectTimeoutHandle); connectTimeoutHandle = null; }
    clearPendingTimers();
  }

  function clearPendingTimers() {
    if (pendingTimeoutHandle) { canceller(pendingTimeoutHandle); pendingTimeoutHandle = null; }
    if (pendingProbeHandle) { canceller(pendingProbeHandle); pendingProbeHandle = null; }
    if (pendingLimitHandle) { canceller(pendingLimitHandle); pendingLimitHandle = null; }
    if (reattachDecideHandle) { canceller(reattachDecideHandle); reattachDecideHandle = null; }
  }

  /**
   * Set or end the wait for a game command's answer, and tell the view.
   * Ending it tells listeners why: `answer` is the frame that answered the
   * command, or null when something else ended the wait (`reason`).
   * @param {{ type: string, idempotency_key: string }|null} next
   * @param {{ answer?: any, reason: string }} [why]
   */
  function setPendingCommand(next, why = { reason: 'cleared' }) {
    clearPendingTimers();
    if (next === null) pendingSocket = null;
    if (pendingCommand === next) return;
    const prev = pendingCommand;
    pendingCommand = next;
    const settled = next === null && prev
      ? { command: prev, answer: why.answer ?? null, reason: why.reason }
      : undefined;
    for (const listener of pendingSubscribers) {
      try { listener(pendingCommand, settled); } catch (e) {
        console.warn('room-ws: a pending-command listener failed:', /** @type {any} */ (e)?.message ?? e);
      }
    }
  }

  /**
   * Subscribe to the game command that waits for its answer (null when
   * none waits). Fires the current value at once. Returns unsubscribe.
   * @param {(pending: { type: string, idempotency_key: string }|null) => void} listener
   */
  function subscribePendingCommand(listener) {
    pendingSubscribers.add(listener);
    listener(pendingCommand);
    return () => { pendingSubscribers.delete(listener); };
  }

  /**
   * Schedule a timeout. Some tests pass a scheduler that runs the callback
   * at once (meaning "retry now"); a timeout that fires before it was even
   * scheduled is meaningless, so that call is ignored.
   * @param {() => void} fn
   * @param {number} ms
   */
  function scheduleTimeout(fn, ms) {
    let armed = false;
    const handle = scheduler(() => { if (armed) fn(); }, ms);
    armed = true;
    return handle;
  }

  /** True while the waiting command's socket is still the live one. */
  function pendingOnLiveSocket() {
    return !!pendingCommand && ws === pendingSocket && wsOpen;
  }

  /**
   * No answer in COMMAND_ANSWER_TIMEOUT_MS: ask the socket if it is alive.
   * Any server frame in the next COMMAND_PROBE_TIMEOUT_MS says yes.
   */
  function commandAnswerTimedOut() {
    pendingTimeoutHandle = null;
    // A socket that is already gone (heartbeat, close) is on the retry path.
    if (!pendingOnLiveSocket()) return;
    unansweredPings += 1;
    sendRaw({ type: 'PING', payload: { client_ts: now() } });
    pendingProbeHandle = scheduleTimeout(commandProbeTimedOut, COMMAND_PROBE_TIMEOUT_MS);
  }

  /** No frame at all after the probe PING: the socket is dead. */
  function commandProbeTimedOut() {
    pendingProbeHandle = null;
    if (!pendingOnLiveSocket()) return;
    onError({ code: 'WS_COMMAND_TIMEOUT', message: `no answer to ${/** @type {any} */ (pendingCommand).type} in ${COMMAND_ANSWER_TIMEOUT_MS} ms and no reply to a PING in ${COMMAND_PROBE_TIMEOUT_MS} ms`, recoverable: true });
    abandonSocket();
  }

  /** A live socket that still has no answer COMMAND_LIVE_LIMIT_MS after the send. */
  function commandLiveLimitReached() {
    pendingLimitHandle = null;
    if (!pendingOnLiveSocket()) return;
    onError({ code: 'WS_COMMAND_NO_ANSWER', message: `no answer to ${/** @type {any} */ (pendingCommand).type} in ${COMMAND_LIVE_LIMIT_MS} ms on a live connection; dropping it and reconnecting`, recoverable: true });
    abandonSocket();
  }

  function startPing() {
    if (pingHandle) intervalCanceller(pingHandle);
    if (deadCheckHandle) intervalCanceller(deadCheckHandle);
    pingHandle = intervalScheduler(() => {
      if (!wsOpen) return;
      unansweredPings += 1;
      sendRaw({
        type: 'PING',
        payload: { client_ts: now() },
      });
    }, pingIntervalMs);
    // Two unanswered PINGs and DEAD_AFTER_MS of silence: the link is dead.
    // Requiring the PINGs too means a throttled background tab (timers
    // that fire late) is never taken for a dead link.
    deadCheckHandle = intervalScheduler(() => {
      if (!wsOpen || unansweredPings < 2) return;
      if (now() - lastFrameAt >= DEAD_AFTER_MS) dropDeadSocket();
    }, DEAD_CHECK_MS);
  }

  /** Close a socket the service no longer uses. Its handlers are already ignored. */
  function closeStale(sock) {
    try { sock.close(); } catch (e) {
      console.warn('room-ws: closing a stale socket failed:', /** @type {any} */ (e)?.message ?? e);
    }
  }

  /**
   * Stop using the current socket at once (its late events are ignored),
   * close it, and go to the retry path. The browser's own close event can
   * take a long time on a dead link, so nothing waits for it.
   */
  function abandonSocket() {
    const sock = ws;
    ws = null;
    wsOpen = false;
    clearTimers();
    if (sock) closeStale(sock);
    scheduleReconnect();
  }

  /** The heartbeat saw no frame for DEAD_AFTER_MS: drop the socket and retry. */
  function dropDeadSocket() {
    onError({ code: 'WS_HEARTBEAT_TIMEOUT', message: `no server frame for ${DEAD_AFTER_MS} ms`, recoverable: true });
    abandonSocket();
  }

  function nextBackoffMs() {
    const i = Math.min(backoffStep, BACKOFF_SCHEDULE_MS.length - 1);
    backoffStep += 1;
    return BACKOFF_SCHEDULE_MS[i];
  }

  /**
   * W5: forget the room code so a later reconnect does NOT replay
   * JOIN_ROOM. Called when the user leaves (via LEAVE_ROOM in sendRaw
   * below) or is removed (the hook calls this when it sees the removal;
   * the service itself cannot tell a kick for us from one for someone
   * else). Without this, LEAVE_ROOM → reconnect → JOIN_ROOM →
   * ROOM_NOT_FOUND lands a stale "No room with that code." on the home
   * screen.
   */
  function forgetRoom() {
    lastRoomCode = null;
    clearGameReplayBuffer();
    setPendingCommand(null, { reason: 'forgotten' });
  }

  function scheduleReconnect() {
    if (manualClose) return;
    const delay = nextBackoffMs();
    reconnectAttempt += 1;
    reconnectDueAt = now() + delay;
    setStatus('reconnecting', true);
    reconnectHandle = scheduler(() => {
      reconnectHandle = null;
      reconnectDueAt = null;
      openSocket();
    }, delay);
  }

  function openSocket() {
    // One attempt at a time: a socket that is still connecting stays the
    // only one. An older socket is never left running next to a new one.
    if (ws && !wsOpen) return;
    if (ws) {
      const old = ws;
      ws = null;
      wsOpen = false;
      closeStale(old);
    }
    /** @type {any} */
    let sock;
    let WSCtor;
    try {
      WSCtor = resolveCtor();
    } catch (e) {
      onError({ code: 'WS_CONSTRUCT_FAILED', message: /** @type {any} */ (e)?.message ?? String(e), recoverable: false });
      // Nothing retries from here, so no answer can come: end the wait.
      setPendingCommand(null, { reason: 'gave_up' });
      setStatus('closed');
      return;
    }
    try {
      sock = new WSCtor(url);
    } catch (e) {
      onError({ code: 'WS_CONSTRUCT_FAILED', message: /** @type {any} */ (e)?.message ?? String(e), recoverable: false });
      // Nothing retries from here, so no answer can come: end the wait.
      setPendingCommand(null, { reason: 'gave_up' });
      setStatus('closed');
      return;
    }
    ws = sock;
    wsOpen = false;
    // A hanging attempt (a network that drops packets) is given up after
    // CONNECT_TIMEOUT_MS and goes back to the retry path.
    if (connectTimeoutHandle) canceller(connectTimeoutHandle);
    connectTimeoutHandle = scheduleTimeout(() => {
      connectTimeoutHandle = null;
      if (ws !== sock || wsOpen) return;
      onError({ code: 'WS_CONNECT_TIMEOUT', message: `the connection did not open in ${CONNECT_TIMEOUT_MS} ms`, recoverable: true });
      abandonSocket();
    }, CONNECT_TIMEOUT_MS);
    // Force a fresh notice on a retry so views redraw "Connecting…" now.
    if (status === 'reconnecting') setStatus('reconnecting', true);
    else setStatus('connecting');
    sock.addEventListener('open', () => {
      if (ws !== sock) { closeStale(sock); return; }
      wsOpen = true;
      if (connectTimeoutHandle) { canceller(connectTimeoutHandle); connectTimeoutHandle = null; }
      lastFrameAt = now();
      unansweredPings = 0;
      backoffStep = 0;
      reconnectAttempt = 0;
      reconnectDueAt = null;
      // A command sent on an older socket: the reattach GAME_INITIALIZED
      // normally decides it. If that never comes, end the wait anyway.
      if (pendingCommand && pendingSocket !== sock) {
        const waiting = pendingCommand;
        reattachDecideHandle = scheduleTimeout(() => {
          reattachDecideHandle = null;
          if (pendingCommand !== waiting || ws !== sock) return;
          console.warn(`room-ws: no game snapshot ${REATTACH_DECIDE_MS} ms after the reattach; ending the wait for ${waiting.type}`);
          setPendingCommand(null, { reason: 'reattach_timeout' });
        }, REATTACH_DECIDE_MS);
      }
      setStatus('open');
      // Replay JOIN_ROOM if we were previously in a room (reconnect
      // recovery path, server responds with ROOM_SNAPSHOT).
      if (lastRoomCode) {
        sendRaw({
          type: 'JOIN_ROOM',
          payload: { room_code: lastRoomCode, identity },
        });
      }
      // Flush queued commands.
      const flushed = outboundQueue;
      outboundQueue = [];
      for (const c of flushed) sendRaw(c);
      startPing();
    });
    sock.addEventListener('message', (ev) => {
      if (ws !== sock) return;
      lastFrameAt = now();
      unansweredPings = 0;
      // Any frame answers the probe PING: the socket is alive, so the
      // command keeps waiting (up to COMMAND_LIVE_LIMIT_MS).
      if (pendingProbeHandle) { canceller(pendingProbeHandle); pendingProbeHandle = null; }
      /** @type {any} */
      let parsed;
      try { parsed = JSON.parse(/** @type {any} */ (ev).data); }
      catch {
        onError({ code: 'MALFORMED_EVENT', message: 'server sent non-JSON frame', recoverable: true });
        return;
      }
      // Watch for ROOM_CREATED / ROOM_JOINED to cache the room_code for
      // reconnect replay.
      if (parsed && typeof parsed === 'object') {
        // The answer to the waiting game command: COMMAND_ACCEPTED, an
        // error, or the game event it caused all name its key. A reattach
        // snapshot (or a room back in the lobby) decides instead.
        if (pendingCommand) {
          const answered = parsed.causation_idempotency_key === pendingCommand.idempotency_key;
          const roomPhase = parsed.payload?.room_state?.phase;
          const roomReset = (parsed.type === 'ROOM_SNAPSHOT' || parsed.type === 'ROOM_JOINED') && roomPhase !== 'in_game';
          // The server no longer counts this socket in the room: nothing
          // will answer the command.
          const notInRoom = parsed.type === 'COMMAND_REJECTED' && parsed.payload?.error?.code === 'NOT_IN_ROOM';
          if (answered) setPendingCommand(null, { answer: parsed, reason: 'answered' });
          else if (parsed.type === 'GAME_INITIALIZED') setPendingCommand(null, { reason: 'reattach' });
          else if (roomReset) setPendingCommand(null, { reason: 'room_reset' });
          else if (notInRoom) setPendingCommand(null, { reason: 'not_in_room' });
        }
        if (parsed.type === 'ROOM_CREATED' && parsed.payload?.room_code) {
          lastRoomCode = parsed.payload.room_code;
        } else if (parsed.type === 'ROOM_JOINED' && parsed.payload?.room_state?.room_code) {
          lastRoomCode = parsed.payload.room_state.room_code;
        } else if (parsed.type === 'PONG') {
          ingestPong(parsed.payload, now());
        } else if (parsed.type === 'GAME_STARTING') {
          // The next snapshot belongs to a new game.
          clearGameReplayBuffer();
        } else if (parsed.type === 'GAME_INITIALIZED' && parsed.payload?.reattach) {
          // A fresh reattach replaces any events queued from an old socket.
          clearGameReplayBuffer();
        } else if (parsed.type === 'ROOM_SNAPSHOT') {
          const phase = parsed.payload?.room_state?.phase ?? parsed.payload?.phase;
          if (phase !== 'in_game') {
            clearGameReplayBuffer();
          }
        }
        // Keep all game frames that arrive without a game subscriber.
        if (extraSubscribers.size === 0 && parsed.type
          && (/^(GAME_|BID_|CARD_|TRICK_|HAND_|PLAY_|DEALER_|SEAT_|BOT_)/.test(parsed.type))
          && parsed.type !== 'GAME_STARTING') {
          pushGameReplayFrame(parsed);
        }
      }
      try { onEvent(parsed); } catch (e) { console.warn('[room-ws] onEvent listener threw:', e); }
      // Fan out to additional subscribers (M4-10 useGameWs etc.).
      for (const sub of extraSubscribers) {
        try { sub(parsed); } catch (e) { console.warn('[room-ws] game subscriber threw:', e); }
      }
    });
    sock.addEventListener('close', () => {
      if (ws !== sock) return;
      clearTimers();
      ws = null;
      wsOpen = false;
      if (manualClose) {
        setStatus('closed');
        return;
      }
      scheduleReconnect();
    });
    sock.addEventListener('error', () => {
      if (ws !== sock) return;
      onError({ code: 'WS_ERROR', message: 'socket error', recoverable: true });
    });
  }

  /** The current socket fired 'open' and the browser still says OPEN. */
  function socketReady() {
    if (!ws || !wsOpen || status !== 'open') return false;
    const state = /** @type {any} */ (ws).readyState;
    return state === undefined || state === 1;
  }

  /**
   * Send a raw frame straight to the socket if open; otherwise queue.
   * Used for PING and the internal reconnect-time JOIN_ROOM replay,
   * which don't need a fresh idempotency_key.
   */
  function sendRaw(command) {
    const enriched = {
      ...command,
      protocol_version: command.protocol_version ?? PROTOCOL_VERSION,
    };
    // W5: leaving forgets the room synchronously, even when the socket
    // is down and the frame only queues. A later reconnect must not
    // rejoin a room the user chose to leave.
    if (command.type === 'LEAVE_ROOM') {
      lastRoomCode = null;
      clearGameReplayBuffer();
      setPendingCommand(null, { reason: 'left' });
    }
    // The server's validateEnvelope requires client_command_seq +
    // idempotency_key on every non-INTERNAL command, PING included.
    // PING isn't replayed on reconnect though, so skip pendingByKey.
    enriched.client_command_seq = command.client_command_seq ?? seq++;
    enriched.idempotency_key    = command.idempotency_key    ?? generateUUIDv4();
    if (command.type !== 'PING') {
      pendingByKey.set(enriched.idempotency_key, enriched);
    }
    if (socketReady()) {
      try { /** @type {any} */ (ws).send(JSON.stringify(enriched)); }
      catch (e) { onError({ code: 'WS_SEND_FAILED', message: /** @type {any} */ (e)?.message ?? String(e), recoverable: true }); }
    } else {
      // PING is best-effort and skipped while disconnected; everything
      // else queues for the next open.
      if (command.type !== 'PING') outboundQueue.push(enriched);
    }
    return enriched;
  }

  /**
   * Public: send a client-originated command. Stamps protocol_version,
   * client_command_seq, idempotency_key (if absent). Caller may pass an
   * idempotency_key to opt into retry semantics.
   *
   * @param {any} command
   * @returns {any} the enriched command actually sent (or queued)
   */
  function send(command) {
    return sendRaw(command);
  }

  /**
   * M4-10: send a game command (BID, PLAY_CARD, DEALER_DISCARD,
   * MARK_HAND_READY, RECLAIM_SEAT). Identical wrapping to `send` —
   * named separately so callers signal intent and so the M4 envelope
   * fields (idempotency_key for retry, expected_room_version for CAS
   * once we wire it) can diverge from room-side semantics later.
   *
   * @param {any} command
   * @returns {any} the enriched command actually sent (or queued)
   */
  function sendGameCommand(command) {
    // A game move must not wait in a queue and fire later: by then the
    // turn may be gone. The view disables its controls while not open;
    // this refuses anything that still gets through.
    if (!socketReady()) {
      onError({ code: 'WS_NOT_OPEN', message: `${command?.type ?? 'command'} not sent: the connection is ${status}`, recoverable: true });
      return null;
    }
    // One move at a time: the next waits for this one's answer.
    if (pendingCommand) {
      onError({ code: 'WS_COMMAND_PENDING', message: `${command?.type ?? 'command'} not sent: ${pendingCommand.type} is still waiting for its answer`, recoverable: true });
      return null;
    }
    const sent = sendRaw(command);
    setPendingCommand({ type: sent.type, idempotency_key: sent.idempotency_key });
    pendingSocket = ws;
    pendingTimeoutHandle = scheduleTimeout(commandAnswerTimedOut, COMMAND_ANSWER_TIMEOUT_MS);
    pendingLimitHandle = scheduleTimeout(commandLiveLimitReached, COMMAND_LIVE_LIMIT_MS);
    return sent;
  }

  /**
   * M4-10: register an extra event subscriber (e.g., useGameWs). Returns
   * an unsubscribe function. The primary onEvent callback still fires
   * for every frame regardless.
   * @param {(ev: any) => void} listener
   */
  function _subscribeForGame(listener) {
    extraSubscribers.add(listener);
    // Replay any game events that landed before this subscriber mounted.
    // Without this, GAME_INITIALIZED is missed by the very first
    // NetworkedGameView mount and the table sticks on "Loading game…".
    if (gameReplayBuffer.length > 0) {
      const snapshot = gameReplayBuffer.slice();
      gameReplayBuffer = [];
      // Queue as a microtask so listeners don't reentrantly mutate state
      // mid-subscribe (matches the original "event arrives later" model).
      Promise.resolve().then(() => {
        for (const ev of snapshot) {
          try { listener(ev); } catch (e) { console.warn('[room-ws] replay subscriber threw:', e); }
        }
      });
    }
    return () => extraSubscribers.delete(listener);
  }

  /** M4-14: current clock-offset estimate (server_ts - local_ts), EWMA-smoothed. */
  function getClockOffset() { return { clockOffsetMs, rttMs: clockRttMs }; }

  /**
   * M4-14: subscribe to clock-offset updates. Listener fires after
   * every PONG. Returns an unsubscribe function.
   * @param {(o:{clockOffsetMs:number, rttMs:number}) => void} listener
   */
  function subscribeClockOffset(listener) {
    clockOffsetSubscribers.add(listener);
    return () => clockOffsetSubscribers.delete(listener);
  }

  function connect() {
    if (status === 'open' || status === 'connecting' || status === 'reconnecting') return;
    manualClose = false;
    openSocket();
  }

  function reconnectNow() {
    // Nothing to do while open, and never a second socket while one is
    // still connecting (its result is on the way).
    if (status === 'open' || (ws && !wsOpen)) return;
    if (reconnectHandle) { canceller(reconnectHandle); reconnectHandle = null; }
    reconnectDueAt = null;
    manualClose = false;
    openSocket();
  }

  function getReconnectInfo() {
    return { attempt: Math.max(1, reconnectAttempt), nextRetryMs: reconnectDueAt == null ? 0 : Math.max(0, reconnectDueAt - now()) };
  }

  /**
   * What the connection is doing now, for the banner:
   *   'open'       the socket is open
   *   'connecting' a socket attempt is in flight
   *   'waiting'    a retry is scheduled (getReconnectInfo has the delay)
   *   'stopped'    nothing will retry until someone calls reconnectNow
   * @returns {'open'|'connecting'|'waiting'|'stopped'}
   */
  function getConnectionPhase() {
    if (ws && wsOpen && status === 'open') return 'open';
    if (ws) return 'connecting';
    if (reconnectHandle) return 'waiting';
    return 'stopped';
  }

  /**
   * e2e seam: drop the live socket as if the network had cut it. Unlike
   * disconnect(), the service treats it as unexpected and retries with its
   * backoff, the same path the heartbeat takes.
   */
  function _dropSocketForTest() {
    if (ws) abandonSocket();
  }

  function disconnect() {
    manualClose = true;
    clearTimers();
    setPendingCommand(null, { reason: 'disconnected' });
    if (ws) {
      const old = ws;
      ws = null;
      wsOpen = false;
      closeStale(old);
    }
    setStatus('closed');
  }

  function getStatus() { return status; }
  function getSeq() { return seq; }
  function getRoomCode() { return lastRoomCode; }
  function _setRoomCodeForTest(code) { lastRoomCode = code; }

  return {
    send,
    sendGameCommand,
    _subscribeForGame,
    getClockOffset,
    subscribeClockOffset,
    /** Test-only: feed a synthetic PONG into the offset estimator. */
    _ingestPongForTest: ingestPong,
    connect,
    reconnectNow,
    getReconnectInfo,
    getConnectionPhase,
    _dropSocketForTest,
    disconnect,
    getStatus,
    subscribeStatus,
    subscribePendingCommand,
    getPendingCommand: () => pendingCommand,
    getSeq,
    getRoomCode,
    _setRoomCodeForTest,
    forgetRoom,
  };
}
